/**
 * Building a single canonical "PROPERTY-PREFIX - UNIT_NUMBER" label so the
 * UI never falls back to a bare "101" / "Studio" that loses the property
 * context (multiple properties have a unit "101").
 *
 * Prefix priority:
 *   1. unit.property_name        (the joined shape some endpoints return)
 *   2. propertyMap[unit.property_id].name
 *   3. first numeric chunk of the property address
 *   4. property name itself (non-numeric — e.g. "Wyn 315", "Test Property")
 *
 * `propertyMap` is the convenient { [id]: property } shape; pass either
 * that or an array of properties — we accept both.
 */

function pickPrefix(unit, propsByIdOrArray) {
  if (!unit) return '';
  if (unit.property_name) return String(unit.property_name).trim();

  // Normalize a list of properties into a {id: prop} map.
  let map = propsByIdOrArray;
  if (Array.isArray(propsByIdOrArray)) {
    map = {};
    for (const p of propsByIdOrArray) {
      if (p && p.id) map[p.id] = p;
    }
  }
  const prop = map && unit.property_id ? map[unit.property_id] : null;
  if (!prop) return '';

  // The property name is the strongest signal — most are already short
  // numeric prefixes ("2166", "1144", "Wyn 315"). When the name is generic
  // ("Test Property") we fall back to the first numeric run of the address.
  const name = (prop.name || '').trim();
  const looksGeneric = !name || /\bproperty\b|\btest\b|untitled/i.test(name);
  if (!looksGeneric) return name;

  const addr = String(prop.address || '').trim();
  const digits = addr.match(/\d+/);
  if (digits) return digits[0];
  return name || '';
}

/**
 * Returns "<prefix> - <unit_number>", or just the unit number when no
 * prefix can be determined (so we never render a stray dash).
 */
export function formatUnitLabel(unit, propsByIdOrArray) {
  if (!unit) return '';
  const num = (unit.unit_number == null ? '' : String(unit.unit_number)).trim();
  const prefix = pickPrefix(unit, propsByIdOrArray);
  if (prefix && num) return `${prefix} - ${num}`;
  return prefix || num;
}

// Build a {id: prop} map from either an array or already-mapped object.
function toPropMap(propsByIdOrArray) {
  if (!propsByIdOrArray) return {};
  if (Array.isArray(propsByIdOrArray)) {
    const m = {};
    for (const p of propsByIdOrArray) if (p && p.id) m[p.id] = p;
    return m;
  }
  return propsByIdOrArray;
}

/**
 * Sort units so dropdowns / lists group by property first (using the
 * property's `building_id`, which the Properties page already orders 1..N),
 * then alphanumerically by unit_number within the property. Properties
 * without a `building_id` fall to the end. Stable for matching keys.
 */
export function sortUnitsByProperty(units, propsByIdOrArray) {
  const map = toPropMap(propsByIdOrArray);
  const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

  const keyFor = (u) => {
    const prop = u && u.property_id ? map[u.property_id] : null;
    const bid = prop && prop.building_id != null ? Number(prop.building_id) : null;
    // Units whose property has no building_id sort after those that do,
    // ordered by the same prefix we display so the visual order is sensible.
    const propName = prop?.name || u?.property_name || '';
    return {
      hasBid: bid != null && !Number.isNaN(bid) ? 0 : 1,
      bid: bid != null && !Number.isNaN(bid) ? bid : 0,
      propName: String(propName),
      unitNum: String(u?.unit_number ?? ''),
    };
  };

  return [...(units || [])].sort((a, b) => {
    const ka = keyFor(a); const kb = keyFor(b);
    if (ka.hasBid !== kb.hasBid) return ka.hasBid - kb.hasBid;
    if (ka.bid !== kb.bid) return ka.bid - kb.bid;
    const byName = collator.compare(ka.propName, kb.propName);
    if (byName !== 0) return byName;
    return collator.compare(ka.unitNum, kb.unitNum);
  });
}
